import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControl, Grid, InputLabel, MenuItem, Modal, Select, TextField, Typography } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

const DEFAULT_DOORS = [
  { active: false, gateId: '', icon: '', name: '', openingTime: '', relay: '', type: '' },
  { active: false, gateId: '', icon: '', name: '', openingTime: '', relay: '', type: '' },
  { active: false, gateId: '', icon: '', name: '', openingTime: '', relay: '', type: '' },
  { active: false, gateId: '', icon: '', name: '', openingTime: '', relay: '', type: '' }
];

const BridgeEditModal = ({ data, handleClose, open, handleSave }) => {
  const { t } = useTranslation('admin', { keyPrefix: 'bridges' });

  const validateUniqueDoorNames = values => {
    const errors = {};

    // Check if values.doors exists and is an array
    if (!values.doors || !Array.isArray(values.doors)) {
      return errors;
    }

    // Get all active door names (lowercase and trimmed)
    const activeDoorsNames = values.doors.filter(door => door && door.active && door.name).map(door => door.name.trim().toLowerCase());

    // Find names that appear more than once
    const namesCount = {};
    activeDoorsNames.forEach(name => {
      if (name) namesCount[name] = (namesCount[name] || 0) + 1;
    });

    // Get duplicate names (appear more than once)
    const duplicateNames = Object.keys(namesCount).filter(name => namesCount[name] > 1);

    if (duplicateNames.length > 0) {
      errors.doors = [];

      // Mark each door with a duplicate name
      values.doors.forEach((door, index) => {
        if (door && door.active && door.name && duplicateNames.includes(door.name.trim().toLowerCase())) {
          if (!errors.doors[index]) errors.doors[index] = {};
          errors.doors[index].name = t('door_name_duplicate');
        }
      });
    }

    return errors;
  };

  const validationSchema = Yup.object().shape({
    doors: Yup.array().of(
      Yup.object().shape({
        active: Yup.boolean(),
        gateId: Yup.string(),
        icon: Yup.string().when('active', {
          is: true,
          otherwise: schema => schema.notRequired(),
          then: schema => schema.oneOf(['D', 'B', 'T'], t('door_icon_required')).required(t('door_icon_required'))
        }),
        name: Yup.string().when('active', {
          is: true,
          otherwise: schema => schema.notRequired(),
          then: schema => schema.required(t('name_required'))
        }),
        openingTime: Yup.number().when('active', {
          is: true,
          otherwise: schema => schema.notRequired(),
          then: schema => schema.min(1, t('opening_time_min')).max(30, t('opening_time_max')).required(t('opening_time_required'))
        }),
        relay: Yup.string(),
        type: Yup.string().when('active', {
          is: true,
          otherwise: schema => schema.notRequired(),
          then: schema => schema.oneOf(['S', 'ES', 'E'], t('door_type_required')).required(t('door_type_required'))
        })
      })
    ),
    name: Yup.string().required(t('name_required'))
  });

  const [bridge, setBridge] = useState({
    bridge_id: '',
    doors: DEFAULT_DOORS,
    name: ''
  });

  useEffect(() => {
    if (data) {
      const filledDoors = [...data.doors];
      while (filledDoors.length < 4) {
        filledDoors.push({ active: false, gateId: '', icon: '', name: '', openingTime: '', relay: '', type: '' });
      }
      setBridge({
        bridge_id: data.bridge_id || '',
        doors: filledDoors,
        name: data.name || ''
      });
    }
  }, [data]);

  return (
    <Modal onClose={handleClose} open={open}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          left: '50%',
          maxWidth: '95vw', // más amplio, usa el 95% del ancho de la ventana
          overflowY: 'auto',
          p: 4,
          position: 'absolute',
          top: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      >
        <Typography sx={{ fontWeight: 'bold', mb: 2 }} variant="h6">
          {t('modal_title')}
        </Typography>
        <Formik
          enableReinitialize
          initialValues={bridge}
          onSubmit={values => {
            handleSave(values);
            handleClose();
          }}
          validate={values => {
            // First run the Yup validation schema
            try {
              validationSchema.validateSync(values, { abortEarly: false });
            } catch (err) {
              // Yup validation errors are handled separately
            }

            // Then check for duplicate door names
            return validateUniqueDoorNames(values);
          }}
          validationSchema={validationSchema}
        >
          {({ errors, touched, values, setFieldValue, setErrors }) => (
            <Form>
              <Box mb={2}>
                <Typography fontWeight="bold">{t('name')}</Typography>
                <Field
                  as={TextField}
                  error={touched.name && !!errors.name}
                  fullWidth
                  helperText={touched.name && errors.name}
                  name="name"
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Box>
                <Typography fontWeight="bold">{t('doors')}</Typography>
                {values.doors.map((door, index) => {
                  const isDisabled = !door.active;
                  return (
                    <Grid alignItems="center" container key={index} mb={2} spacing={1}>
                      <Grid item>
                        <Checkbox
                          checked={door.active}
                          onChange={e => {
                            const newActive = e.target.checked;
                            setFieldValue(`doors[${index}].active`, newActive);
                            if (!newActive) {
                              setErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors.doors?.[index]?.name;
                                delete newErrors.doors?.[index]?.openingTime;
                                delete newErrors.doors?.[index]?.type;
                                return newErrors;
                              });
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={0.5}>
                        <Typography fontWeight="bold">{index + 1}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Field
                          as={TextField}
                          disabled={isDisabled}
                          error={touched.doors?.[index]?.name && !!errors.doors?.[index]?.name}
                          fullWidth
                          helperText={touched.doors?.[index]?.name && errors.doors?.[index]?.name}
                          label={t('door_name')}
                          name={`doors[${index}].name`}
                          size="small"
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <Field
                          as={TextField}
                          disabled={isDisabled}
                          error={touched.doors?.[index]?.openingTime && !!errors.doors?.[index]?.openingTime}
                          fullWidth
                          helperText={touched.doors?.[index]?.openingTime && errors.doors?.[index]?.openingTime}
                          label={t('door_opening_time')}
                          name={`doors[${index}].openingTime`}
                          size="small"
                          type="number"
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={2.5}>
                        <FormControl disabled={isDisabled} fullWidth size="small" variant="outlined">
                          <InputLabel id={`door-type-label-${index}`} shrink sx={{ px: 1 }}>
                            {t('door_type')}
                          </InputLabel>
                          <Field
                            as={Select}
                            displayEmpty
                            label={t('door_type')}
                            labelId={`door-type-label-${index}`}
                            margin="dense"
                            name={`doors[${index}].type`}
                            value={door.type}
                          >
                            <MenuItem disabled value="">
                              {t('select_type')}
                            </MenuItem>
                            <MenuItem title={t('exitInfo')} value="S">
                              {t('exit')}
                            </MenuItem>
                            <MenuItem title={t('entranceInfo')} value="E">
                              {t('entrance')}
                            </MenuItem>
                            <MenuItem title={t('entrance_exitInfo')} value="ES">
                              {t('entrance_exit')}
                            </MenuItem>
                          </Field>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2.5}>
                        <FormControl disabled={isDisabled} fullWidth size="small" variant="outlined">
                          <InputLabel id={`door-icon-label-${index}`} shrink sx={{ px: 1 }}>
                            {t('door_icon')}
                          </InputLabel>
                          <Field
                            as={Select}
                            displayEmpty
                            label={t('door_icon')}
                            labelId={`door-icon-label-${index}`}
                            margin="dense"
                            name={`doors[${index}].icon`}
                            onChange={e => {
                              setFieldValue(`doors[${index}].icon`, e.target.value);
                            }}
                            value={door.icon || ''}
                          >
                            <MenuItem disabled value="">
                              {t('select_icon')}
                            </MenuItem>
                            <MenuItem value="D">{t('door')}</MenuItem>
                            <MenuItem value="B">{t('bar')}</MenuItem>
                            <MenuItem value="T">{t('turnstile')}</MenuItem>
                          </Field>
                        </FormControl>
                      </Grid>
                    </Grid>
                  );
                })}
              </Box>
              <Box display="flex" justifyContent="space-between" mt={2}>
                <Button color="secondary" onClick={handleClose} sx={{ textTransform: 'none' }} variant="contained">
                  {t('close')}
                </Button>
                <Button color="primary" onClick={() => handleSave(values)} type="submit" variant="contained">
                  {t('save')}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};
export default BridgeEditModal;
